/**
 * Epic-specific Skeleton Loaders
 * 
 * Provides skeleton loading components specifically designed for epic-related UI components
 * to improve perceived performance during data loading.
 */

import React from 'react'
import clsx from 'clsx'
import { Skeleton, SkeletonText } from './ProgressIndicators'

// ===== Epic Grid Skeleton =====

export const EpicGridSkeleton: React.FC<{ rows?: number; className?: string }> = ({
  rows = 5,
  className = ''
}) => {
  return (
    <div className={clsx('space-y-0', className)}>
      {/* Table header skeleton */}
      <div className="bg-gray-50 px-6 py-3 border-b border-gray-200">
        <div className="grid grid-cols-4 gap-4">
          <Skeleton width="80px" height="12px" />
          <Skeleton width="120px" height="12px" />
          <Skeleton width="100px" height="12px" />
          <Skeleton width="60px" height="12px" />
        </div>
      </div>

      {/* Table rows skeleton */}
      {Array.from({ length: rows }).map((_, index) => (
        <div key={index} className="bg-white px-6 py-4 border-b border-gray-200">
          <div className="grid grid-cols-4 gap-4 items-center">
            {/* Epic key */}
            <div>
              <Skeleton width="80px" height="16px" />
            </div>

            {/* Title and completion */}
            <div className="space-y-2">
              <Skeleton width="200px" height="16px" />
              <Skeleton width="120px" height="12px" />
            </div>

            {/* Status */}
            <div>
              <Skeleton width="80px" height="24px" rounded />
            </div>

            {/* Actions */}
            <div>
              <Skeleton width="60px" height="28px" rounded />
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

// ===== Epic Stats Skeleton =====

export const EpicStatsSkeleton: React.FC<{ className?: string }> = ({
  className = ''
}) => {
  return (
    <div className={clsx('bg-white p-6 rounded-lg shadow-sm border border-gray-200', className)}>
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <Skeleton width="120px" height="16px" />
          <Skeleton width="80px" height="24px" />
        </div>
        <div className="space-y-2">
          <Skeleton width="100px" height="12px" />
          <Skeleton width="60px" height="12px" />
        </div>
      </div>
    </div>
  )
}

// ===== Epic Filters Skeleton =====

export const EpicFiltersSkeleton: React.FC<{ className?: string }> = ({
  className = ''
}) => {
  return (
    <div className={clsx('bg-white p-6 rounded-lg shadow-sm border border-gray-200', className)}>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Project selector */}
        <div>
          <Skeleton width="60px" height="16px" className="mb-2" />
          <Skeleton width="100%" height="40px" rounded />
        </div>

        {/* Start date */}
        <div>
          <Skeleton width="80px" height="16px" className="mb-2" />
          <Skeleton width="100%" height="40px" rounded />
        </div>

        {/* End date */}
        <div>
          <Skeleton width="70px" height="16px" className="mb-2" />
          <Skeleton width="100%" height="40px" rounded />
        </div>
      </div>
    </div>
  )
}

// ===== Batch Controller Skeleton =====

export const BatchControllerSkeleton: React.FC<{ className?: string }> = ({
  className = ''
}) => {
  return (
    <div className={clsx('bg-white p-6 rounded-lg shadow-sm border border-gray-200', className)}>
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <Skeleton width="140px" height="16px" />
          <Skeleton width="200px" height="12px" />
        </div>
        <Skeleton width="120px" height="36px" rounded />
      </div>
    </div>
  )
}

// ===== Epic Details Page Skeleton =====

export const EpicDetailsPageSkeleton: React.FC<{ className?: string }> = ({
  className = ''
}) => {
  return (
    <div className={clsx('max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8', className)}>
      {/* Header */}
      <div className="mb-8">
        <Skeleton width="300px" height="32px" className="mb-4" />
        <Skeleton width="500px" height="16px" />
      </div>

      {/* Metrics cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {Array.from({ length: 4 }).map((_, index) => (
          <div key={index} className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <Skeleton width="80px" height="16px" />
              <Skeleton width="24px" height="24px" rounded />
            </div>
            <Skeleton width="60px" height="28px" className="mb-2" />
            <Skeleton width="100px" height="12px" />
          </div>
        ))}
      </div>

      {/* Charts section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        {Array.from({ length: 2 }).map((_, index) => (
          <div key={index} className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <Skeleton width="200px" height="20px" className="mb-4" />
            <Skeleton width="100%" height="300px" rounded />
          </div>
        ))}
      </div>

      {/* Timeline section */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <Skeleton width="150px" height="20px" className="mb-6" />
        <div className="space-y-4">
          {Array.from({ length: 5 }).map((_, index) => (
            <div key={index} className="flex items-start space-x-4">
              <Skeleton width="12px" height="12px" rounded />
              <div className="flex-1 space-y-2">
                <Skeleton width="200px" height="16px" />
                <Skeleton width="300px" height="12px" />
              </div>
              <Skeleton width="80px" height="12px" />
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// ===== Epic Card Skeleton =====

export const EpicCardSkeleton: React.FC<{ className?: string }> = ({
  className = ''
}) => {
  return (
    <div className={clsx('bg-white p-6 rounded-lg shadow-sm border border-gray-200', className)}>
      <div className="flex items-start justify-between mb-4">
        <div className="space-y-2">
          <Skeleton width="100px" height="16px" />
          <Skeleton width="250px" height="20px" />
        </div>
        <Skeleton width="80px" height="24px" rounded />
      </div>

      <div className="space-y-3">
        <div className="flex justify-between">
          <Skeleton width="80px" height="12px" />
          <Skeleton width="60px" height="12px" />
        </div>
        <div className="flex justify-between">
          <Skeleton width="100px" height="12px" />
          <Skeleton width="40px" height="12px" />
        </div>
        <div className="flex justify-between">
          <Skeleton width="90px" height="12px" />
          <Skeleton width="50px" height="12px" />
        </div>
      </div>

      <div className="mt-4 pt-4 border-t border-gray-200">
        <div className="flex justify-between items-center">
          <Skeleton width="120px" height="12px" />
          <Skeleton width="80px" height="28px" rounded />
        </div>
      </div>
    </div>
  )
}

// ===== Epic List Item Skeleton =====

export const EpicListItemSkeleton: React.FC<{ className?: string }> = ({
  className = ''
}) => {
  return (
    <div className={clsx('border border-gray-200 rounded-lg p-4', className)}>
      <div className="flex items-start justify-between mb-3">
        <div className="space-y-2">
          <Skeleton width="80px" height="14px" />
          <Skeleton width="200px" height="16px" />
        </div>
        <Skeleton width="70px" height="20px" rounded />
      </div>

      <div className="grid grid-cols-2 gap-4 text-sm">
        <div className="space-y-2">
          <div className="flex justify-between">
            <Skeleton width="60px" height="12px" />
            <Skeleton width="40px" height="12px" />
          </div>
          <div className="flex justify-between">
            <Skeleton width="80px" height="12px" />
            <Skeleton width="30px" height="12px" />
          </div>
        </div>
        <div className="space-y-2">
          <div className="flex justify-between">
            <Skeleton width="70px" height="12px" />
            <Skeleton width="50px" height="12px" />
          </div>
          <div className="flex justify-between">
            <Skeleton width="90px" height="12px" />
            <Skeleton width="35px" height="12px" />
          </div>
        </div>
      </div>
    </div>
  )
}

// ===== Page Loading Skeleton =====

export const EpicPageSkeleton: React.FC<{ className?: string }> = ({
  className = ''
}) => {
  return (
    <div className={clsx('min-h-screen bg-white', className)}>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-start mb-4">
            <div>
              <Skeleton width="200px" height="32px" className="mb-2" />
              <Skeleton width="400px" height="16px" />
            </div>
            <Skeleton width="150px" height="40px" rounded />
          </div>
        </div>

        {/* Filters */}
        <div className="mb-6">
          <EpicFiltersSkeleton />
        </div>

        {/* Stats and Batch Controller */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <EpicStatsSkeleton />
          <BatchControllerSkeleton />
        </div>

        {/* Epic Grid */}
        <div className="bg-white shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
          <EpicGridSkeleton rows={8} />
        </div>
      </div>
    </div>
  )
}

// ===== Shimmer Effect Skeleton =====

export const ShimmerSkeleton: React.FC<{
  width?: string | number
  height?: string | number
  className?: string
}> = ({ width = '100%', height = '1rem', className = '' }) => {
  const widthStyle = typeof width === 'number' ? `${width}px` : width
  const heightStyle = typeof height === 'number' ? `${height}px` : height

  return (
    <div
      className={clsx(
        'relative overflow-hidden bg-gray-200 rounded',
        className
      )}
      style={{ width: widthStyle, height: heightStyle }}
    >
      <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-white/60 to-transparent" />
    </div>
  )
}

export default {
  EpicGridSkeleton,
  EpicStatsSkeleton,
  EpicFiltersSkeleton,
  BatchControllerSkeleton,
  EpicDetailsPageSkeleton,
  EpicCardSkeleton,
  EpicListItemSkeleton,
  EpicPageSkeleton,
  ShimmerSkeleton
}